/**
 * Kunden-Detailseite — Stammdaten + Deployments + gebündeltes
 * Vertragsmanagement.
 *
 * Ein Kunde bündelt mehrere Deployments (Tenants). Die Vertrags-/Lizenz-Sicht
 * hier ist eine READ-TIME-Aggregation über die per-Tenant-Verträge/-Lizenzen
 * (die per-Tenant-Zeilen bleiben die Enforcement-Quelle). Zusätzlich lässt
 * sich der kundenseitige Status (aktiv/archiviert) und die Stammdaten pflegen.
 *
 * Backend: GET/PATCH/DELETE /api/customers/:id, GET …/deployments, …/contracts.
 */
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  CustomerView,
  CustomerInput,
  CustomerDeployment,
  CustomerContractsAggregate,
  getCustomer,
  updateCustomer,
  deleteCustomer,
  listCustomerDeployments,
  getCustomerContracts,
  extractApiError,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { labelForCustomerStatus, labelForTenantStatus } from '../services/status-labels';
import { formatBytes } from '../services/format';
import { CustomerBillingSection } from '../components/CustomerBillingSection';


export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;

  const [customer, setCustomer] = useState<CustomerView | null>(null);
  const [deployments, setDeployments] = useState<CustomerDeployment[]>([]);
  const [agg, setAgg] = useState<CustomerContractsAggregate | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  function load(): void {
    if (!id) return;
    getCustomer(id).then(setCustomer).catch((e) => setErr(extractApiError(e)));
    listCustomerDeployments(id).then(setDeployments).catch(() => setDeployments([]));
    getCustomerContracts(id).then(setAgg).catch(() => setAgg(null));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  async function patch(input: CustomerInput): Promise<void> {
    if (!id) return;
    setBusy(true); setErr(null);
    try {
      setCustomer(await updateCustomer(id, input));
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!id || !customer) return;
    if (!window.confirm(
      `Kunde "${customer.companyName}" löschen? Zugeordnete Deployments (${customer.tenantCount}) laufen weiter, `
      + 'ihre Kunden-Zuordnung wird nur entfernt.',
    )) return;
    setBusy(true); setErr(null);
    try {
      await deleteCustomer(id);
      navigate('/customers');
    } catch (e) {
      setErr(extractApiError(e));
      setBusy(false);
    }
  }

  if (!customer) {
    return <p className="text-slate-500">{err ?? 'Lade…'}</p>;
  }

  return (
    <div className="max-w-5xl">
      <Link to="/customers" className="text-sm text-primary-600 hover:underline">← Kunden</Link>
      <div className="flex items-center justify-between mt-1 mb-4">
        <div>
          <h2 className="text-2xl font-semibold">{customer.companyName}</h2>
          <p className="text-slate-500 text-sm">
            Marke: <span className="font-mono">{customer.brand}</span> ·{' '}
            <span className="badge bg-slate-200">{labelForCustomerStatus(customer.status)}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing((s) => !s)} disabled={isReadOnly} className="px-3 py-1 rounded border disabled:opacity-50">
            {editing ? 'Bearbeitung schließen' : 'Bearbeiten'}
          </button>
          <button
            onClick={() => patch({ status: customer.status === 'active' ? 'archived' : 'active' })}
            disabled={busy || isReadOnly}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            {customer.status === 'active' ? 'Archivieren' : 'Reaktivieren'}
          </button>
          <button onClick={remove} disabled={busy || isReadOnly} className="btn-danger disabled:opacity-50">Löschen</button>
        </div>
      </div>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3">{err}</div>}

      {editing && !isReadOnly ? (
        <EditForm customer={customer} busy={busy} onSave={async (input) => { await patch(input); setEditing(false); }} onCancel={() => setEditing(false)} />
      ) : (
        <div className="bg-white rounded shadow p-4 mb-6 grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <Row label="Anzeigename" value={customer.displayName} />
          <Row label="USt-ID" value={customer.vatId} mono />
          <Row label="Adresse" value={[customer.addressLine1, customer.addressLine2].filter(Boolean).join(', ') || null} />
          <Row label="Ort" value={[customer.postalCode, customer.city].filter(Boolean).join(' ') || null} />
          <Row label="Land" value={customer.country} mono />
          <Row label="Rechnungs-E-Mail" value={customer.billingEmail} />
          <Row label="Rechnungskontakt" value={customer.billingContact} />
          <Row label="Externe Referenz" value={customer.externalRef} mono />
        </div>
      )}

      <h3 className="font-semibold mb-2">Deployments ({deployments.length})</h3>
      <table className="w-full bg-white shadow rounded mb-6 text-sm">
        <thead className="bg-slate-100 text-left">
          <tr><th className="p-2">Slug</th><th className="p-2">Name</th><th className="p-2">Plan</th><th className="p-2">Status</th></tr>
        </thead>
        <tbody>
          {deployments.map((d) => (
            <tr key={d.slug} className="border-t hover:bg-slate-50">
              <td className="p-2 font-mono">
                <Link to={`/tenants/${d.slug}`} className="text-primary-600 hover:underline">{d.slug}</Link>
              </td>
              <td className="p-2">{d.name}</td>
              <td className="p-2"><span className="badge bg-slate-200">{d.plan}</span></td>
              <td className="p-2">{labelForTenantStatus(d.status)}</td>
            </tr>
          ))}
          {deployments.length === 0 && (
            <tr><td colSpan={4} className="p-4 text-center text-slate-500">
              Keine Deployments zugeordnet. Zuordnung erfolgt in der Tenant-Detailansicht.
            </td></tr>
          )}
        </tbody>
      </table>

      <h3 className="font-semibold mb-2">Vertragsmanagement (gebündelt)</h3>
      {agg ? (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Stat label="Storage-Quota (Σ)" value={formatBytes(agg.totals.storageQuotaBytes)} />
            <Stat label="AI-Token-Quota (Σ)" value={`${agg.totals.aiTokensUsed.toLocaleString()} / ${agg.totals.aiTokenQuota.toLocaleString()}`} />
            <Stat label="S3-Buckets (Σ)" value={String(agg.totals.bucketCount)} />
            <Stat label="Seats (Σ)" value={`${agg.totals.usedSeats} / ${agg.totals.seats}`} />
            <Stat label="Verträge" value={`${agg.contractsProvisioned} / ${agg.deploymentCount}`} />
            <Stat label="Lizenzen" value={`${agg.licensesProvisioned} / ${agg.deploymentCount}`} />
          </div>
          <table className="w-full bg-white shadow rounded text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-2">Deployment</th><th className="p-2">Vertrag</th><th className="p-2">Storage</th>
                <th className="p-2">AI-Token</th><th className="p-2">Buckets</th><th className="p-2">Seats</th>
              </tr>
            </thead>
            <tbody>
              {agg.deployments.map((d) => (
                <tr key={d.slug} className="border-t">
                  <td className="p-2 font-mono">
                    <Link to={`/tenants/${d.slug}`} className="text-primary-600 hover:underline">{d.slug}</Link>
                  </td>
                  <td className="p-2">{d.contract ? d.contract.status : <span className="text-slate-400">kein Vertrag</span>}</td>
                  <td className="p-2">{d.contract ? formatBytes(d.contract.storageQuotaBytes) : '—'}</td>
                  <td className="p-2">{d.contract ? `${d.contract.aiTokensUsed.toLocaleString()} / ${d.contract.aiTokenQuota.toLocaleString()}` : '—'}</td>
                  <td className="p-2">{d.contract ? d.contract.bucketCount : '—'}</td>
                  <td className="p-2">{d.license ? `${d.license.usedSeats} / ${d.license.seats}` : '—'}</td>
                </tr>
              ))}
              {agg.deployments.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-slate-500">Keine Deployments → keine Verträge.</td></tr>
              )}
            </tbody>
          </table>
          <p className="text-xs text-slate-400 mt-2">
            Aggregiert aus den per-Deployment-Verträgen/-Lizenzen. Die Pflege einzelner
            Verträge erfolgt in der jeweiligen Tenant-Detailansicht.
          </p>
        </>
      ) : (
        <p className="text-slate-500">Keine Vertragsdaten.</p>
      )}

      {/* Abrechnung — Vorschau der Periode + bisherige Abrechnungen dieses Kunden. */}
      <CustomerBillingSection customerId={customer.id} />
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="flex gap-2 py-1">
      <span className="w-40 text-slate-500">{label}</span>
      <span className={mono ? 'font-mono' : ''}>{value || <span className="text-slate-400">—</span>}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-mono mt-1">{value}</p>
    </div>
  );
}

interface EditFormProps {
  customer: CustomerView;
  busy: boolean;
  onSave: (input: CustomerInput) => void;
  onCancel: () => void;
}

function EditForm({ customer, busy, onSave, onCancel }: EditFormProps) {
  const [form, setForm] = useState<CustomerInput>({
    companyName: customer.companyName,
    displayName: customer.displayName,
    addressLine1: customer.addressLine1,
    addressLine2: customer.addressLine2,
    postalCode: customer.postalCode,
    city: customer.city,
    country: customer.country,
    vatId: customer.vatId,
    billingEmail: customer.billingEmail,
    billingContact: customer.billingContact,
    externalRef: customer.externalRef,
  });
  function set<K extends keyof CustomerInput>(key: K, value: CustomerInput[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }
  const fields: Array<[keyof CustomerInput, string]> = [
    ['companyName', 'Firma *'], ['displayName', 'Anzeigename'], ['vatId', 'USt-ID'],
    ['addressLine1', 'Adresse 1'], ['addressLine2', 'Adresse 2'], ['postalCode', 'PLZ'],
    ['city', 'Ort'], ['country', 'Land (ISO-2)'], ['billingEmail', 'Rechnungs-E-Mail'],
    ['billingContact', 'Rechnungskontakt'], ['externalRef', 'Externe Referenz'],
  ];
  return (
    <div className="bg-white rounded shadow p-4 mb-6 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([key, label]) => (
          <div key={String(key)}>
            <label className="block text-sm font-medium mb-1">{label}</label>
            <input
              value={(form[key] as string | null) ?? ''}
              onChange={(e) => set(key, (key === 'country' || key === 'vatId' ? e.target.value.toUpperCase() : e.target.value) as CustomerInput[typeof key])}
              className="w-full px-2 py-1 rounded border text-sm"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1 rounded border" disabled={busy}>Abbrechen</button>
        <button type="button" onClick={() => onSave(form)} className="btn-primary" disabled={busy}>
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
